/**
 * @file src/components/Answer/Answer.tsx
 * @exports Answer
 * @summary Flexible component for rendering FAQ answers in multiple formats (string, paragraphs, or bullet points).
 */

import { BodyLPrimary } from "@cerebruminc/cerebellum";
import _ from "lodash";
import React from "react";
import styled from "styled-components";

const AnswerContainer = styled(BodyLPrimary)`
	line-height: 30px;
	p {
		font-size: 18px;
	}
`;

const Paragraph = styled.p`
	margin: 0 0 1em 0;
	&:last-child {
		margin-bottom: 0;
	}
`;

const BulletList = styled.ul`
	margin: 0 0 1em 0;
	padding-left: 1.5em;
	&:last-child {
		margin-bottom: 0;
	}
`;

const BulletItem = styled.li`
	margin-bottom: 0.5em;
	padding-left: 15px;
	list-style-type: disc;
	&:last-child {
		margin-bottom: 0;
	}
`;

interface AnswerProps {
	content: string | Array<string | Array<string>>;
}

export const Answer: React.FC<AnswerProps> = ({ content }) => {
	// Handle string: render as single paragraph
	if (typeof content === "string") {
		return (
			<AnswerContainer>
				<Paragraph>{content}</Paragraph>
			</AnswerContainer>
		);
	}

	// Handle array
	return (
		<AnswerContainer>
			{content.map((item) => {
				// If item is an array, render as bullet points
				if (Array.isArray(item)) {
					return (
						<BulletList key={_.uniqueId("bullet_list_")}>
							{item.map((bullet) => (
								<BulletItem key={_.uniqueId("bullet_item_")}>{bullet}</BulletItem>
							))}
						</BulletList>
					);
				}
				// If item is a string, render as paragraph
				return <Paragraph key={_.uniqueId("paragraph_")}>{item}</Paragraph>;
			})}
		</AnswerContainer>
	);
};
